import React, { useState, useEffect } from 'react';
import {
  ChevronDownIcon,
  InfoIcon,
  XIcon,
  SlidersIcon,
  BotIcon,
  TrophyIcon,
  RotateCcwIcon,
  DownloadIcon,
  UploadIcon,
  AlertCircleIcon,
  UserIcon,
  BarChart3Icon,
  BrainIcon,
  CodeIcon,
  PenToolIcon,
  SearchIcon,
  CalculatorIcon,
  BriefcaseIcon,
  LightbulbIcon,
  TrendingUpIcon,
  StarIcon,
  ZapIcon,
  FlameIcon,
  TargetIcon,
  AwardIcon,
  Trash2Icon,
  SettingsIcon,
  DollarSignIcon,
  UsersIcon,
  ToggleLeftIcon,
  ToggleRightIcon
} from 'lucide-react';
import { useSettings, DEFAULT_SCORING_PARAMS } from '../SettingsContext';
import personalities from '../utils/personalities';
import CustomPersonasTab from './CustomPersonasTab';
import { mergePersonalities } from '../utils/personalitiesLoader';
import {
  getGlobalStats,
  updateGlobalStats,
  getForgeMemory,
  updateForgeMemory,
  getAllCategories,
  getRecommendation,
  clearForgeMemory,
  MODEL_COLORS
} from '../utils/globalStats';

const modelOptions = {
  gpt: [
    { id: 'gpt-4o-mini', name: 'GPT-4o Mini' },
    { id: 'gpt-4o', name: 'GPT-4o' },
    { id: 'gpt-4.1', name: 'GPT-4.1' },
    { id: 'gpt-4.1-mini', name: 'GPT-4.1 Mini' },
    { id: 'gpt-5', name: 'GPT-5' },
    { id: 'gpt-5-mini', name: 'GPT-5 Mini' },
    { id: 'gpt-5-nano', name: 'GPT-5 Nano' }
  ],
  claude: [
    { id: 'claude-opus-4-20250514', name: 'Claude Opus 4' },
    { id: 'claude-opus-4-1-20250805', name: 'Claude Opus 4.1' },
    { id: 'claude-sonnet-4-20250514', name: 'Claude Sonnet 4' },
    { id: 'claude-3-haiku-20240307', name: 'Claude 3 Haiku' }
  ],
  gemini: [
    { id: 'gemini-2.5-flash', name: 'Gemini 2.5 Flash' },
    { id: 'gemini-2.5-flash-lite', name: 'Gemini 2.5 Flash Lite' }
  ]
};

const scoringPresets = {
  balanced: {
    name: 'Balanced (Default)',
    description: 'Equal emphasis on all criteria',
    weights: DEFAULT_SCORING_PARAMS.weights
  },
  speedFocused: {
    name: 'Speed Focused',
    description: 'Prioritizes fast responses',
    weights: {
      success: 25,
      speed: 30,
      completeness: 15,
      accuracy: 15,
      readability: 5,
      structure: 5,
      depth: 5
    }
  },
  qualityFirst: {
    name: 'Quality First',
    description: 'Prioritizes accuracy and completeness',
    weights: {
      success: 20,
      speed: 5,
      completeness: 25,
      accuracy: 30,
      readability: 5,
      structure: 10,
      depth: 5
    }
  },
  detailOriented: {
    name: 'Detail Oriented',
    description: 'Favors thorough and well-structured responses',
    weights: {
      success: 20,
      speed: 5,
      completeness: 20,
      accuracy: 20,
      readability: 5,
      structure: 15,
      depth: 15
    }
  }
};

const SettingsModal = ({ onClose, threads = [] }) => {
  const token = localStorage.getItem("neuroforge_token");
  const {
    settings,
    updateSetting,
    updateScoringWeights,
    updateScoringThresholds,
    resetScoringToDefaults,
    setViewMode,
    setSoloAgent
  } = useSettings();

  const [activeCategory, setActiveCategory] = useState('general');
  const [scoringExplanationOpen, setScoringExplanationOpen] = useState(false);
  const [customPersonas, setCustomPersonas] = useState([]);
  const [personasLoaded, setPersonasLoaded] = useState(false);
  const [globalStats, setGlobalStats] = useState({
    gpt: { roundWins: 0, tournamentWins: 0 },
    claude: { roundWins: 0, tournamentWins: 0 },
    gemini: { roundWins: 0, tournamentWins: 0 }
  });
  const [forgeMemory, setForgeMemory] = useState(null);
  const [selectedMemoryCategory, setSelectedMemoryCategory] = useState('global');
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [tokenCosts, setTokenCosts] = useState(null);
  const [tokenCostsLoading, setTokenCostsLoading] = useState(false);

  // Function to load custom personas (extracted for reusability)
  const loadCustomPersonas = async () => {
    try {
      const response = await fetch('/api/custom-personas', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCustomPersonas(data.personas || []);
      } else {
        console.log('[SettingsModal] /api/custom-personas response not OK:', response.status);
      }
    } catch (error) {
      console.error('[SettingsModal] Failed to load custom personas:', error);
    } finally {
      setPersonasLoaded(true);
    }
  };

  // Function to load token costs
  const loadTokenCosts = async () => {
    if (!token) return;
    setTokenCostsLoading(true);
    try {
      const response = await fetch('/api/admin/token-costs', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTokenCosts(data);
      } else {
        console.log('[SettingsModal] Failed to load token costs:', response.status);
      }
    } catch (error) {
      console.error('[SettingsModal] Failed to load token costs:', error);
    } finally {
      setTokenCostsLoading(false);
    }
  };

  // Load custom personas on mount
  useEffect(() => {

    if (token) {
      loadCustomPersonas();
    } else {
      console.log('[SettingsModal] token is falsy, NOT loading personas');
    }
  }, [token]);

  // Load token costs when costs tab is active
  useEffect(() => {
    if (activeCategory === 'costs' && !tokenCosts && !tokenCostsLoading) {
      loadTokenCosts();
    }
  }, [activeCategory]);

  // Fetch full thread data and recalculate global stats + Forge Memory
  const recalculateStatsFromAPI = async () => {
    try {
      // Fetch all threads with full data
      const response = await fetch('/api/threads', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch threads');
      }

      const data = await response.json();

      // Now fetch each thread's full details (with messages)
      const fullThreads = await Promise.all(
        data.threads.map(async (threadSummary) => {
          const threadResponse = await fetch(`/api/threads/${threadSummary.id}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (threadResponse.ok) {
            const threadData = await threadResponse.json();
            return threadData.thread;
          }
          return null;
        })
      );

      // Filter out any failed fetches
      const validThreads = fullThreads.filter(t => t !== null);

      // Recalculate and save stats + Forge Memory
      const stats = updateGlobalStats(validThreads);
      setGlobalStats(stats);

      // Also update Forge Memory
      const memory = updateForgeMemory(validThreads);
      setForgeMemory(memory);
    } catch (error) {
      console.error('[SettingsModal] Failed to recalculate stats from API:', error);
      // Fallback to localStorage
      const stats = getGlobalStats();
      const memory = getForgeMemory();
      setGlobalStats(stats);
      setForgeMemory(memory);
    }
  };

  // Handle reset Forge Memory
  const handleResetForgeMemory = () => {
    clearForgeMemory();
    setForgeMemory(null);
    setGlobalStats({
      gpt: { roundWins: 0, tournamentWins: 0 },
      claude: { roundWins: 0, tournamentWins: 0 },
      gemini: { roundWins: 0, tournamentWins: 0 }
    });
    setShowResetConfirm(false);
    // Trigger recalculation from API
    recalculateStatsFromAPI();
  };

  // Recalculate stats when modal opens
  useEffect(() => {
    if (token) {
      recalculateStatsFromAPI();
    }
  }, [token]);

  // Also reload stats when switching to statistics tab
  useEffect(() => {
    if (activeCategory === 'statistics' && token) {
      recalculateStatsFromAPI();
    }
  }, [activeCategory]);

  // Merge custom personas with built-in personalities for each agent
  const mergedPersonalities = {
    gpt: mergePersonalities(customPersonas, 'gpt'),
    claude: mergePersonalities(customPersonas, 'claude'),
    gemini: mergePersonalities(customPersonas, 'gemini')
  };

  // Calculate total weight
  const totalWeight = Object.values(settings.scoring.weights).reduce((a, b) => a + b, 0);
  const isWeightValid = totalWeight === 100;

  const handleWeightChange = (key, value) => {
    const numValue = Math.max(0, Math.min(100, parseInt(value) || 0));
    updateScoringWeights({ [key]: numValue });
  };

  const handleThresholdChange = (key, value) => {
    const numValue = Math.max(0, parseInt(value) || 0);
    updateScoringThresholds({ [key]: numValue });
  };

  const applyPreset = (presetKey) => {
    updateScoringWeights(scoringPresets[presetKey].weights);
  };

  const exportSettings = () => {
    const dataStr = JSON.stringify(settings, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    const exportFileDefaultName = 'neuroforge-settings.json';

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const importSettings = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const imported = JSON.parse(e.target.result);
          if (imported.scoring) {
            updateScoringWeights(imported.scoring.weights);
            updateScoringThresholds(imported.scoring.thresholds);
          }
          // Import model settings too
          ['gpt', 'claude', 'gemini'].forEach(agent => {
            if (imported[agent]) {
              Object.entries(imported[agent]).forEach(([key, value]) => {
                updateSetting(agent, key, value);
              });
            }
          });
        } catch (error) {
          alert('Invalid settings file');
        }
      };
      reader.readAsText(file);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-xl sm:rounded-2xl w-full max-w-5xl max-h-[95vh] sm:max-h-[90vh] shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 bg-slate-800/95 backdrop-blur-sm border-b border-slate-700 p-4 sm:p-6 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <SlidersIcon className="h-5 w-5 sm:h-6 sm:w-6 text-cyan-400" />
            <h2 className="text-xl sm:text-2xl font-black text-white">Settings</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg glass border border-slate-600/30 text-slate-400 hover:text-white hover:border-slate-500/50 transition-all"
            title="Close"
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Mobile Tab Navigation */}
        <div className="md:hidden flex-shrink-0 border-b border-slate-700 bg-slate-900/95 backdrop-blur-sm overflow-x-auto">
          <div className="flex p-2 gap-2 min-w-max">
            <button
              onClick={() => setActiveCategory('general')}
              className={`px-4 py-2 rounded-lg transition-all flex items-center gap-2 font-medium whitespace-nowrap ${
                activeCategory === 'general'
                  ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              <SettingsIcon className="h-4 w-4" />
              <span className="text-sm">General</span>
            </button>
            <button
              onClick={() => setActiveCategory('models')}
              className={`px-4 py-2 rounded-lg transition-all flex items-center gap-2 font-medium whitespace-nowrap ${
                activeCategory === 'models'
                  ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              <BotIcon className="h-4 w-4" />
              <span className="text-sm">Models</span>
            </button>
            <button
              onClick={() => setActiveCategory('scoring')}
              className={`px-4 py-2 rounded-lg transition-all flex items-center gap-2 font-medium whitespace-nowrap ${
                activeCategory === 'scoring'
                  ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              <TrophyIcon className="h-4 w-4" />
              <span className="text-sm">Scoring</span>
            </button>
            <button
              onClick={() => setActiveCategory('personas')}
              className={`px-4 py-2 rounded-lg transition-all flex items-center gap-2 font-medium whitespace-nowrap ${
                activeCategory === 'personas'
                  ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              <UserIcon className="h-4 w-4" />
              <span className="text-sm">Personas</span>
            </button>
            <button
              onClick={() => setActiveCategory('statistics')}
              className={`px-4 py-2 rounded-lg transition-all flex items-center gap-2 font-medium whitespace-nowrap ${
                activeCategory === 'statistics'
                  ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              <BrainIcon className="h-4 w-4" />
              <span className="text-sm">Memory</span>
            </button>
            <button
              onClick={() => setActiveCategory('costs')}
              className={`px-4 py-2 rounded-lg transition-all flex items-center gap-2 font-medium whitespace-nowrap ${
                activeCategory === 'costs'
                  ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              <DollarSignIcon className="h-4 w-4" />
              <span className="text-sm">Costs</span>
            </button>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar Navigation - Desktop Only */}
          <div className="hidden md:block w-48 bg-slate-900/50 border-r border-slate-700 p-4 space-y-2">
            <button
              onClick={() => setActiveCategory('general')}
              className={`w-full text-left px-4 py-3 rounded-lg transition-all flex items-center gap-3 font-medium ${
                activeCategory === 'general'
                  ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              <SettingsIcon className="h-5 w-5" />
              <span>General</span>
            </button>
            <button
              onClick={() => setActiveCategory('models')}
              className={`w-full text-left px-4 py-3 rounded-lg transition-all flex items-center gap-3 font-medium ${
                activeCategory === 'models'
                  ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              <BotIcon className="h-5 w-5" />
              <span>AI Models</span>
            </button>
            <button
              onClick={() => setActiveCategory('scoring')}
              className={`w-full text-left px-4 py-3 rounded-lg transition-all flex items-center gap-3 font-medium ${
                activeCategory === 'scoring'
                  ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              <TrophyIcon className="h-5 w-5" />
              <span>Scoring</span>
            </button>
            <button
              onClick={() => setActiveCategory('personas')}
              className={`w-full text-left px-4 py-3 rounded-lg transition-all flex items-center gap-3 font-medium ${
                activeCategory === 'personas'
                  ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              <UserIcon className="h-5 w-5" />
              <span>Personas</span>
            </button>
            <button
              onClick={() => setActiveCategory('statistics')}
              className={`w-full text-left px-4 py-3 rounded-lg transition-all flex items-center gap-3 font-medium ${
                activeCategory === 'statistics'
                  ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              <BrainIcon className="h-5 w-5" />
              <span>Memory</span>
            </button>
            <button
              onClick={() => setActiveCategory('costs')}
              className={`w-full text-left px-4 py-3 rounded-lg transition-all flex items-center gap-3 font-medium ${
                activeCategory === 'costs'
                  ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              <DollarSignIcon className="h-5 w-5" />
              <span>Costs</span>
            </button>
          </div>

          {/* Main Content Area */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            {/* General Category - View Mode Toggle */}
            {activeCategory === 'general' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                    <SettingsIcon className="h-5 w-5 text-emerald-400" />
                    General Settings
                  </h3>
                  <p className="text-slate-400 text-sm mb-6">
                    Configure your NeuroForge experience and interaction mode.
                  </p>
                </div>

                {/* View Mode Toggle */}
                <div className="glass border border-slate-700/50 rounded-xl p-5">
                  <h4 className="text-lg text-emerald-400 font-bold mb-4">Interaction Mode</h4>
                  <p className="text-slate-400 text-sm mb-4">
                    Choose how you want to interact with AI agents.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Tournament Mode */}
                    <button
                      onClick={() => setViewMode('tournament')}
                      className={`p-4 rounded-lg border-2 transition-all text-left ${
                        settings.viewMode === 'tournament'
                          ? 'border-cyan-500 bg-cyan-500/10'
                          : 'border-slate-600 hover:border-slate-500 bg-slate-800/50'
                      }`}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`p-2 rounded-lg ${settings.viewMode === 'tournament' ? 'bg-cyan-500/20' : 'bg-slate-700'}`}>
                          <TrophyIcon className={`h-5 w-5 ${settings.viewMode === 'tournament' ? 'text-cyan-400' : 'text-slate-400'}`} />
                        </div>
                        <span className={`font-bold ${settings.viewMode === 'tournament' ? 'text-cyan-300' : 'text-white'}`}>
                          Tournament Mode
                        </span>
                      </div>
                      <p className="text-slate-400 text-sm">
                        All 3 AI agents respond simultaneously. Compare responses, run tournaments, and crown winners.
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <span className="text-xs px-2 py-1 bg-slate-700 rounded text-slate-300">3 AIs at once</span>
                        <span className="text-xs px-2 py-1 bg-slate-700 rounded text-slate-300">Scoring & Analysis</span>
                        <span className="text-xs px-2 py-1 bg-slate-700 rounded text-slate-300">Winners</span>
                      </div>
                    </button>

                    {/* Solo Mode */}
                    <button
                      onClick={() => setViewMode('solo')}
                      className={`p-4 rounded-lg border-2 transition-all text-left ${
                        settings.viewMode === 'solo'
                          ? 'border-emerald-500 bg-emerald-500/10'
                          : 'border-slate-600 hover:border-slate-500 bg-slate-800/50'
                      }`}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`p-2 rounded-lg ${settings.viewMode === 'solo' ? 'bg-emerald-500/20' : 'bg-slate-700'}`}>
                          <UserIcon className={`h-5 w-5 ${settings.viewMode === 'solo' ? 'text-emerald-400' : 'text-slate-400'}`} />
                        </div>
                        <span className={`font-bold ${settings.viewMode === 'solo' ? 'text-emerald-300' : 'text-white'}`}>
                          Solo Mode
                        </span>
                      </div>
                      <p className="text-slate-400 text-sm">
                        Chat with one AI at a time. Switch between agents mid-conversation while keeping context.
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <span className="text-xs px-2 py-1 bg-slate-700 rounded text-slate-300">1 AI at a time</span>
                        <span className="text-xs px-2 py-1 bg-slate-700 rounded text-slate-300">Switch agents</span>
                        <span className="text-xs px-2 py-1 bg-slate-700 rounded text-slate-300">Shared context</span>
                      </div>
                    </button>
                  </div>

                  {settings.viewMode === 'solo' && (
                    <div className="mt-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                      <p className="text-emerald-300 text-sm">
                        <strong>Solo Mode Active:</strong> You can now chat with individual AI agents. Switch between GPT, Claude, and Gemini at any time while maintaining conversation context.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* AI Models Category */}
            {activeCategory === 'models' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                    <BotIcon className="h-5 w-5 text-cyan-400" />
                    AI Model Configuration
                  </h3>
                  <p className="text-slate-400 text-sm mb-6">
                    Customize which models and personalities each AI agent uses in battles.
                  </p>
                </div>

                {['gpt', 'claude', 'gemini'].map(agent => (
                  <div key={agent} className="glass border border-slate-700/50 rounded-xl p-5">
                    <h4 className="text-lg text-cyan-400 capitalize font-bold mb-4">{agent}</h4>

                    {/* Personality */}
                    <div className="mb-4">
                      <label className="block text-slate-300 text-sm font-semibold mb-2">Personality</label>
                      <select
                        className="w-full bg-slate-900 text-white p-3 rounded-lg border border-slate-600 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/50 transition-all"
                        value={settings[agent].personalityId}
                        onChange={(e) => updateSetting(agent, 'personalityId', e.target.value)}
                      >
                      {/* Built-in Personalities */}
                      <optgroup label="Built-in Personalities">
                        {Object.entries(mergedPersonalities[agent])
                          .filter(([id, p]) => !p.isCustom)
                          .map(([id, p]) => (
                            <option key={id} value={id}>
                              {p.name} - {p.description}
                            </option>
                          ))}
                      </optgroup>

                      {/* Custom Personas */}
                      {Object.entries(mergedPersonalities[agent])
                        .filter(([id, p]) => p.isCustom)
                        .length > 0 && (
                        <optgroup label="Custom Personas">
                          {Object.entries(mergedPersonalities[agent])
                            .filter(([id, p]) => p.isCustom)
                            .map(([id, p]) => (
                              <option key={id} value={id}>
                                {p.name} - {p.description}
                              </option>
                            ))}
                        </optgroup>
                      )}
                    </select>
                    </div>

                    {/* Model */}
                    <div>
                      <label className="block text-slate-300 text-sm font-semibold mb-2">Model</label>
                      <select
                        className="w-full bg-slate-900 text-white p-3 rounded-lg border border-slate-600 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/50 transition-all"
                        value={settings[agent].model}
                        onChange={(e) => updateSetting(agent, 'model', e.target.value)}
                      >
                        {modelOptions[agent].map(opt => (
                          <option key={opt.id} value={opt.id}>{opt.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Scoring Category */}
            {activeCategory === 'scoring' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                    <TrophyIcon className="h-5 w-5 text-cyan-400" />
                    Scoring & Analysis Configuration
                  </h3>
                  <p className="text-slate-400 text-sm mb-6">
                    Customize how NeuroForge evaluates and scores AI responses to determine winners.
                  </p>
                </div>

                {/* Quick Actions */}
                <div className="glass border border-slate-700/50 rounded-xl p-5">
                  <h4 className="text-lg font-bold text-white mb-4">Quick Actions</h4>
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={resetScoringToDefaults}
                      className="flex items-center gap-2 px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 text-white rounded-lg transition-all border border-slate-600/50"
                    >
                      <RotateCcwIcon className="h-4 w-4" />
                      Reset to Defaults
                    </button>
                    <button
                      onClick={exportSettings}
                      className="flex items-center gap-2 px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 text-white rounded-lg transition-all border border-slate-600/50"
                    >
                      <DownloadIcon className="h-4 w-4" />
                      Export Settings
                    </button>
                    <label className="flex items-center gap-2 px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 text-white rounded-lg transition-all border border-slate-600/50 cursor-pointer">
                      <UploadIcon className="h-4 w-4" />
                      Import Settings
                      <input
                        type="file"
                        accept=".json"
                        onChange={importSettings}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>

                {/* Presets */}
                <div className="glass border border-slate-700/50 rounded-xl p-5">
                  <h4 className="text-lg font-bold text-white mb-4">Scoring Presets</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {Object.entries(scoringPresets).map(([key, preset]) => (
                      <button
                        key={key}
                        onClick={() => applyPreset(key)}
                        className="text-left p-4 glass border border-slate-600/30 rounded-lg hover:border-cyan-500/50 hover:bg-slate-700/30 transition-all"
                      >
                        <div className="font-bold text-white mb-1">{preset.name}</div>
                        <div className="text-xs text-slate-400">{preset.description}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Weight Configuration */}
                <div className="glass border border-slate-700/50 rounded-xl p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-bold text-white">Score Weights</h4>
                    <div className={`px-3 py-1 rounded-lg text-sm font-bold ${
                      isWeightValid
                        ? 'bg-green-500/20 text-green-300 border border-green-500/30'
                        : 'bg-red-500/20 text-red-300 border border-red-500/30'
                    }`}>
                      Total: {totalWeight}%
                    </div>
                  </div>

                  {!isWeightValid && (
                    <div className="mb-4 p-3 bg-red-900/30 border border-red-600/50 rounded-lg flex items-start gap-2">
                      <AlertCircleIcon className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <p className="text-red-300 text-sm">
                        Weights must total exactly 100%. Current total: {totalWeight}%
                      </p>
                    </div>
                  )}

                  <div className="space-y-4">
                    {Object.entries(settings.scoring.weights).map(([key, value]) => (
                      <div key={key}>
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-slate-300 font-semibold capitalize">
                            {key}
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              min="0"
                              max="100"
                              value={value}
                              onChange={(e) => handleWeightChange(key, e.target.value)}
                              className="w-16 bg-slate-900 text-white p-2 rounded-lg border border-slate-600 text-center focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/50"
                            />
                            <span className="text-slate-400 text-sm font-medium">%</span>
                          </div>
                        </div>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={value}
                          onChange={(e) => handleWeightChange(key, e.target.value)}
                          className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-cyan-500"
                        />
                        <div className="mt-1 text-xs text-slate-500">
                          {key === 'success' && 'No crashes, hallucinations, or incomplete answers'}
                          {key === 'speed' && 'Response time - faster is better'}
                          {key === 'completeness' && 'Fully addresses the prompt with no gaps'}
                          {key === 'accuracy' && 'On-topic, authoritative, factually aligned'}
                          {key === 'readability' && 'Flesch Reading Ease + grammar analysis'}
                          {key === 'structure' && 'Proper use of bullets, headings, formatting'}
                          {key === 'depth' && 'Level of detail and thoroughness'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Threshold Configuration */}
                <div className="glass border border-slate-700/50 rounded-xl p-5">
                  <h4 className="text-lg font-bold text-white mb-4">Response Thresholds</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-slate-300 text-sm font-semibold mb-2">
                        Max Response Time (seconds)
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={settings.scoring.thresholds.maxResponseTime}
                        onChange={(e) => handleThresholdChange('maxResponseTime', e.target.value)}
                        className="w-full bg-slate-900 text-white p-3 rounded-lg border border-slate-600 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/50"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-300 text-sm font-semibold mb-2">
                        Min Tokens
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={settings.scoring.thresholds.minTokens}
                        onChange={(e) => handleThresholdChange('minTokens', e.target.value)}
                        className="w-full bg-slate-900 text-white p-3 rounded-lg border border-slate-600 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/50"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-300 text-sm font-semibold mb-2">
                        Max Tokens
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={settings.scoring.thresholds.maxTokens}
                        onChange={(e) => handleThresholdChange('maxTokens', e.target.value)}
                        className="w-full bg-slate-900 text-white p-3 rounded-lg border border-slate-600 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/50"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-300 text-sm font-semibold mb-2">
                        Min Words
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={settings.scoring.thresholds.minWords}
                        onChange={(e) => handleThresholdChange('minWords', e.target.value)}
                        className="w-full bg-slate-900 text-white p-3 rounded-lg border border-slate-600 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/50"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-300 text-sm font-semibold mb-2">
                        Target Words
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={settings.scoring.thresholds.targetWords}
                        onChange={(e) => handleThresholdChange('targetWords', e.target.value)}
                        className="w-full bg-slate-900 text-white p-3 rounded-lg border border-slate-600 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/50"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-300 text-sm font-semibold mb-2">
                        Max Words
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={settings.scoring.thresholds.maxWords}
                        onChange={(e) => handleThresholdChange('maxWords', e.target.value)}
                        className="w-full bg-slate-900 text-white p-3 rounded-lg border border-slate-600 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/50"
                      />
                    </div>
                  </div>
                </div>

                {/* Scoring Explanation */}
                <div className="glass border border-slate-700/50 rounded-xl overflow-hidden">
                  <button
                    onClick={() => setScoringExplanationOpen(!scoringExplanationOpen)}
                    className="w-full text-left p-5 hover:bg-slate-700/30 transition-colors flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <InfoIcon className="h-5 w-5 text-cyan-400" />
                      <span className="text-white font-bold">How Scoring & Analysis Works</span>
                    </div>
                    <ChevronDownIcon
                      className={`h-5 w-5 text-slate-400 transition-transform ${
                        scoringExplanationOpen ? 'rotate-180' : ''
                      }`}
                    />
                  </button>

                  {scoringExplanationOpen && (
                    <div className="p-5 bg-slate-900/30 border-t border-slate-700">
                      <div className="space-y-4 text-slate-300 text-sm">
                        {/* Overview */}
                        <div>
                          <h4 className="text-base font-semibold text-cyan-400 mb-2">Overview</h4>
                          <p className="leading-relaxed">
                            NeuroForge evaluates AI responses across multiple dimensions using a weighted scoring system.
                            Each response is scored out of 100 points based on the criteria you configure above. The highest
                            scoring response wins the round. You can fully customize the weights and thresholds to match your
                            preferences and use cases.
                          </p>
                        </div>

                        {/* How Weights Work */}
                        <div>
                          <h4 className="text-base font-semibold text-cyan-400 mb-2">Understanding Weights</h4>
                          <p className="leading-relaxed mb-2">
                            Weights determine how much each criterion contributes to the final score. For example:
                          </p>
                          <ul className="list-disc list-inside space-y-1 ml-2">
                            <li><strong>Success (30%):</strong> If this is high, responses with no errors are heavily favored</li>
                            <li><strong>Speed (10%):</strong> Lower weight means speed matters less than quality</li>
                            <li><strong>Completeness (20%):</strong> Prioritizes responses that fully address the prompt</li>
                            <li><strong>Accuracy (20%):</strong> Favors factually correct and on-topic responses</li>
                          </ul>
                          <p className="leading-relaxed mt-2">
                            <strong className="text-yellow-300">Important:</strong> All weights must total exactly 100%.
                            Adjust weights to match your priorities (e.g., increase "Speed" for time-sensitive tasks).
                          </p>
                        </div>

                        {/* How Thresholds Work */}
                        <div>
                          <h4 className="text-base font-semibold text-cyan-400 mb-2">Understanding Thresholds</h4>
                          <p className="leading-relaxed mb-2">
                            Thresholds define acceptable ranges for responses:
                          </p>
                          <ul className="list-disc list-inside space-y-1 ml-2">
                            <li><strong>Max Response Time:</strong> Responses slower than this receive speed penalties</li>
                            <li><strong>Token Limits:</strong> Responses outside min/max token range may score lower</li>
                            <li><strong>Word Counts:</strong> Target word count is optimal; too short or too long gets penalized</li>
                          </ul>
                        </div>

                        {/* Impact of Adjustments */}
                        <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-4">
                          <h4 className="text-base font-semibold text-yellow-300 mb-2">Impact on Winner Selection</h4>
                          <p className="leading-relaxed mb-2">
                            Adjusting these parameters directly affects which AI wins each battle. For instance:
                          </p>
                          <ul className="list-disc list-inside space-y-1 ml-2">
                            <li>Increasing <strong>Speed</strong> weight: Faster models (like GPT) may win more often</li>
                            <li>Increasing <strong>Depth</strong> weight: Models with thorough responses (like Claude) gain advantage</li>
                            <li>Lowering <strong>Max Words</strong>: Concise responses are favored over verbose ones</li>
                            <li>Using "Quality First" preset: Accuracy and completeness matter most, speed matters least</li>
                          </ul>
                        </div>

                        {/* Analysis Feature */}
                        <div>
                          <h4 className="text-base font-semibold text-cyan-400 mb-2">Analysis Feature</h4>
                          <p className="leading-relaxed">
                            After each battle round, you can use the "Analyze" button on individual responses to have the
                            remaining AIs critique that answer. This progressively eliminates weaker responses until only
                            the ultimate winner remains. The scoring weights you configure apply to both initial battles
                            and subsequent analysis rounds.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Custom Personas Category */}
            {activeCategory === 'personas' && (
              <CustomPersonasTab token={token} onPersonasChange={loadCustomPersonas} />
            )}

            {/* Forge Memory - Enhanced Statistics */}
            {activeCategory === 'statistics' && (
              <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                      <BrainIcon className="h-5 w-5 text-purple-400" />
                      Forge Memory
                    </h3>
                    <p className="text-slate-400 text-sm">
                      Your AI profile based on {forgeMemory?.totalTournaments || 0} tournaments. Track which AI performs best for different tasks.
                    </p>
                  </div>
                  <button
                    onClick={() => setShowResetConfirm(true)}
                    className="p-2 rounded-lg glass border border-slate-600/30 text-slate-400 hover:text-red-400 hover:border-red-500/30 transition-all"
                    title="Reset all statistics"
                  >
                    <Trash2Icon className="h-4 w-4" />
                  </button>
                </div>

                {/* Reset Confirmation Modal */}
                {showResetConfirm && (
                  <div className="glass border border-red-500/30 rounded-xl p-4 bg-red-500/10">
                    <div className="flex items-start gap-3">
                      <AlertCircleIcon className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-white font-medium mb-2">Reset all Forge Memory data?</p>
                        <p className="text-sm text-slate-400 mb-3">This will clear all statistics and recommendations. Data will be rebuilt from your existing threads.</p>
                        <div className="flex gap-2">
                          <button
                            onClick={handleResetForgeMemory}
                            className="px-3 py-1.5 bg-red-500/20 text-red-300 rounded-lg text-sm font-medium hover:bg-red-500/30 transition-all"
                          >
                            Reset
                          </button>
                          <button
                            onClick={() => setShowResetConfirm(false)}
                            className="px-3 py-1.5 bg-slate-700/50 text-slate-300 rounded-lg text-sm font-medium hover:bg-slate-600/50 transition-all"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Category Tabs */}
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setSelectedMemoryCategory('global')}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                      selectedMemoryCategory === 'global'
                        ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                        : 'bg-slate-800/50 text-slate-400 hover:text-white border border-slate-700/30'
                    }`}
                  >
                    <BarChart3Icon className="h-4 w-4" />
                    Overall
                  </button>
                  <button
                    onClick={() => setSelectedMemoryCategory('coding')}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                      selectedMemoryCategory === 'coding'
                        ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30'
                        : 'bg-slate-800/50 text-slate-400 hover:text-white border border-slate-700/30'
                    }`}
                  >
                    <CodeIcon className="h-4 w-4" />
                    Coding
                  </button>
                  <button
                    onClick={() => setSelectedMemoryCategory('creative')}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                      selectedMemoryCategory === 'creative'
                        ? 'bg-pink-500/20 text-pink-300 border border-pink-500/30'
                        : 'bg-slate-800/50 text-slate-400 hover:text-white border border-slate-700/30'
                    }`}
                  >
                    <PenToolIcon className="h-4 w-4" />
                    Creative
                  </button>
                  <button
                    onClick={() => setSelectedMemoryCategory('analysis')}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                      selectedMemoryCategory === 'analysis'
                        ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30'
                        : 'bg-slate-800/50 text-slate-400 hover:text-white border border-slate-700/30'
                    }`}
                  >
                    <SearchIcon className="h-4 w-4" />
                    Analysis
                  </button>
                  <button
                    onClick={() => setSelectedMemoryCategory('math')}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                      selectedMemoryCategory === 'math'
                        ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30'
                        : 'bg-slate-800/50 text-slate-400 hover:text-white border border-slate-700/30'
                    }`}
                  >
                    <CalculatorIcon className="h-4 w-4" />
                    Math
                  </button>
                  <button
                    onClick={() => setSelectedMemoryCategory('business')}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                      selectedMemoryCategory === 'business'
                        ? 'bg-green-500/20 text-green-300 border border-green-500/30'
                        : 'bg-slate-800/50 text-slate-400 hover:text-white border border-slate-700/30'
                    }`}
                  >
                    <BriefcaseIcon className="h-4 w-4" />
                    Business
                  </button>
                  <button
                    onClick={() => setSelectedMemoryCategory('general')}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                      selectedMemoryCategory === 'general'
                        ? 'bg-slate-500/20 text-slate-300 border border-slate-500/30'
                        : 'bg-slate-800/50 text-slate-400 hover:text-white border border-slate-700/30'
                    }`}
                  >
                    <LightbulbIcon className="h-4 w-4" />
                    General
                  </button>
                </div>

                {/* Current Category Stats */}
                {(() => {
                  const categoryData = selectedMemoryCategory === 'global'
                    ? forgeMemory?.global
                    : forgeMemory?.byCategory?.[selectedMemoryCategory];

                  const recommendation = selectedMemoryCategory !== 'global' && categoryData
                    ? getRecommendation(categoryData)
                    : null;

                  const categories = getAllCategories();
                  const categoryInfo = categories[selectedMemoryCategory];

                  // Calculate win rates for display
                  const totalTournamentWins = categoryData
                    ? (categoryData.gpt?.tournamentWins || 0) +
                      (categoryData.claude?.tournamentWins || 0) +
                      (categoryData.gemini?.tournamentWins || 0)
                    : 0;

                  const getWinRate = (model) => {
                    if (!categoryData || totalTournamentWins === 0) return 0;
                    return Math.round(((categoryData[model]?.tournamentWins || 0) / totalTournamentWins) * 100);
                  };

                  return (
                    <>
                      {/* Recommendation Banner */}
                      {recommendation && recommendation.model && totalTournamentWins >= 3 && (
                        <div className={`glass rounded-xl p-4 border ${
                          recommendation.isDominant
                            ? 'border-yellow-500/30 bg-yellow-500/5'
                            : 'border-cyan-500/30 bg-cyan-500/5'
                        }`}>
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${
                              recommendation.isDominant ? 'bg-yellow-500/20' : 'bg-cyan-500/20'
                            }`}>
                              {recommendation.isDominant
                                ? <AwardIcon className="h-5 w-5 text-yellow-400" />
                                : <TargetIcon className="h-5 w-5 text-cyan-400" />
                              }
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-bold text-white">
                                  Recommendation: Start with {recommendation.model.toUpperCase()}
                                </span>
                                {recommendation.confidence === 'high' && (
                                  <span className="px-2 py-0.5 rounded-full bg-green-500/20 text-green-300 text-xs font-medium">
                                    High Confidence
                                  </span>
                                )}
                                {recommendation.confidence === 'medium' && (
                                  <span className="px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-300 text-xs font-medium">
                                    Medium Confidence
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-slate-400 mt-0.5">
                                {recommendation.reason} ({totalTournamentWins} tournaments)
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Not Enough Data Banner */}
                      {totalTournamentWins < 3 && selectedMemoryCategory !== 'global' && (
                        <div className="glass rounded-xl p-4 border border-slate-600/30 bg-slate-800/30">
                          <div className="flex items-center gap-3">
                            <InfoIcon className="h-5 w-5 text-slate-400" />
                            <div>
                              <span className="text-slate-300 font-medium">Not enough data yet</span>
                              <p className="text-sm text-slate-500">
                                Run at least 3 full tournaments in this category to get recommendations.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Stats Cards */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* GPT Card */}
                        <div className={`glass border rounded-xl p-5 shadow-lg transition-all ${
                          recommendation?.model === 'gpt'
                            ? 'border-emerald-500/50 ring-1 ring-emerald-500/20'
                            : 'border-slate-600/50'
                        }`}>
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                                <BotIcon className="h-5 w-5 text-emerald-400" />
                              </div>
                              <div>
                                <div className="text-lg font-bold text-white">GPT</div>
                                <div className="text-xs text-slate-400">OpenAI</div>
                              </div>
                            </div>
                            {recommendation?.model === 'gpt' && (
                              <StarIcon className="h-5 w-5 text-yellow-400" />
                            )}
                          </div>

                          {/* Win Rate Bar */}
                          <div className="mb-4">
                            <div className="flex justify-between text-xs mb-1">
                              <span className="text-slate-400">Win Rate</span>
                              <span className="text-emerald-400 font-bold">{getWinRate('gpt')}%</span>
                            </div>
                            <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-gradient-to-r from-emerald-600 to-emerald-400 transition-all duration-500"
                                style={{ width: `${getWinRate('gpt')}%` }}
                              />
                            </div>
                          </div>

                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-slate-400">Tournament Wins</span>
                              <span className="text-yellow-400 font-bold">{categoryData?.gpt?.tournamentWins || 0}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-400">Round Wins</span>
                              <span className="text-white">{categoryData?.gpt?.roundWins || 0}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-400">Avg Score</span>
                              <span className="text-slate-300">{categoryData?.gpt?.avgScore || 0}</span>
                            </div>
                            {selectedMemoryCategory === 'global' && (
                              <>
                                <div className="h-px bg-slate-700 my-2"></div>
                                <div className="flex justify-between">
                                  <span className="text-slate-400">Current Streak</span>
                                  <span className="text-orange-400 flex items-center gap-1">
                                    {categoryData?.gpt?.winStreak || 0}
                                    {(categoryData?.gpt?.winStreak || 0) >= 3 && <FlameIcon className="h-3 w-3" />}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-slate-400">Best Streak</span>
                                  <span className="text-purple-400">{categoryData?.gpt?.bestWinStreak || 0}</span>
                                </div>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Claude Card */}
                        <div className={`glass border rounded-xl p-5 shadow-lg transition-all ${
                          recommendation?.model === 'claude'
                            ? 'border-orange-500/50 ring-1 ring-orange-500/20'
                            : 'border-slate-600/50'
                        }`}>
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                                <BotIcon className="h-5 w-5 text-orange-400" />
                              </div>
                              <div>
                                <div className="text-lg font-bold text-white">Claude</div>
                                <div className="text-xs text-slate-400">Anthropic</div>
                              </div>
                            </div>
                            {recommendation?.model === 'claude' && (
                              <StarIcon className="h-5 w-5 text-yellow-400" />
                            )}
                          </div>

                          {/* Win Rate Bar */}
                          <div className="mb-4">
                            <div className="flex justify-between text-xs mb-1">
                              <span className="text-slate-400">Win Rate</span>
                              <span className="text-orange-400 font-bold">{getWinRate('claude')}%</span>
                            </div>
                            <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-gradient-to-r from-orange-600 to-orange-400 transition-all duration-500"
                                style={{ width: `${getWinRate('claude')}%` }}
                              />
                            </div>
                          </div>

                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-slate-400">Tournament Wins</span>
                              <span className="text-yellow-400 font-bold">{categoryData?.claude?.tournamentWins || 0}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-400">Round Wins</span>
                              <span className="text-white">{categoryData?.claude?.roundWins || 0}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-400">Avg Score</span>
                              <span className="text-slate-300">{categoryData?.claude?.avgScore || 0}</span>
                            </div>
                            {selectedMemoryCategory === 'global' && (
                              <>
                                <div className="h-px bg-slate-700 my-2"></div>
                                <div className="flex justify-between">
                                  <span className="text-slate-400">Current Streak</span>
                                  <span className="text-orange-400 flex items-center gap-1">
                                    {categoryData?.claude?.winStreak || 0}
                                    {(categoryData?.claude?.winStreak || 0) >= 3 && <FlameIcon className="h-3 w-3" />}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-slate-400">Best Streak</span>
                                  <span className="text-purple-400">{categoryData?.claude?.bestWinStreak || 0}</span>
                                </div>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Gemini Card */}
                        <div className={`glass border rounded-xl p-5 shadow-lg transition-all ${
                          recommendation?.model === 'gemini'
                            ? 'border-blue-500/50 ring-1 ring-blue-500/20'
                            : 'border-slate-600/50'
                        }`}>
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                                <BotIcon className="h-5 w-5 text-blue-400" />
                              </div>
                              <div>
                                <div className="text-lg font-bold text-white">Gemini</div>
                                <div className="text-xs text-slate-400">Google</div>
                              </div>
                            </div>
                            {recommendation?.model === 'gemini' && (
                              <StarIcon className="h-5 w-5 text-yellow-400" />
                            )}
                          </div>

                          {/* Win Rate Bar */}
                          <div className="mb-4">
                            <div className="flex justify-between text-xs mb-1">
                              <span className="text-slate-400">Win Rate</span>
                              <span className="text-blue-400 font-bold">{getWinRate('gemini')}%</span>
                            </div>
                            <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-gradient-to-r from-blue-600 to-blue-400 transition-all duration-500"
                                style={{ width: `${getWinRate('gemini')}%` }}
                              />
                            </div>
                          </div>

                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-slate-400">Tournament Wins</span>
                              <span className="text-yellow-400 font-bold">{categoryData?.gemini?.tournamentWins || 0}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-400">Round Wins</span>
                              <span className="text-white">{categoryData?.gemini?.roundWins || 0}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-400">Avg Score</span>
                              <span className="text-slate-300">{categoryData?.gemini?.avgScore || 0}</span>
                            </div>
                            {selectedMemoryCategory === 'global' && (
                              <>
                                <div className="h-px bg-slate-700 my-2"></div>
                                <div className="flex justify-between">
                                  <span className="text-slate-400">Current Streak</span>
                                  <span className="text-orange-400 flex items-center gap-1">
                                    {categoryData?.gemini?.winStreak || 0}
                                    {(categoryData?.gemini?.winStreak || 0) >= 3 && <FlameIcon className="h-3 w-3" />}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-slate-400">Best Streak</span>
                                  <span className="text-purple-400">{categoryData?.gemini?.bestWinStreak || 0}</span>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Global Summary Stats */}
                      {selectedMemoryCategory === 'global' && forgeMemory && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="glass border border-slate-600/50 rounded-xl p-4 text-center">
                            <div className="text-3xl font-bold text-purple-400">{forgeMemory.totalTournaments || 0}</div>
                            <div className="text-xs text-slate-400 mt-1">Total Tournaments</div>
                          </div>
                          <div className="glass border border-slate-600/50 rounded-xl p-4 text-center">
                            <div className="text-3xl font-bold text-cyan-400">{forgeMemory.totalRounds || 0}</div>
                            <div className="text-xs text-slate-400 mt-1">Total Rounds</div>
                          </div>
                          <div className="glass border border-slate-600/50 rounded-xl p-4 text-center">
                            <div className="text-3xl font-bold text-yellow-400">
                              {Math.max(
                                forgeMemory.global?.gpt?.bestWinStreak || 0,
                                forgeMemory.global?.claude?.bestWinStreak || 0,
                                forgeMemory.global?.gemini?.bestWinStreak || 0
                              )}
                            </div>
                            <div className="text-xs text-slate-400 mt-1">Best Win Streak</div>
                          </div>
                          <div className="glass border border-slate-600/50 rounded-xl p-4 text-center">
                            <div className="text-3xl font-bold text-green-400">
                              {forgeMemory.firstRecordedDate
                                ? Math.floor((new Date() - new Date(forgeMemory.firstRecordedDate)) / (1000 * 60 * 60 * 24))
                                : 0}
                            </div>
                            <div className="text-xs text-slate-400 mt-1">Days Active</div>
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}

                {/* Quick Recommendations Summary */}
                {forgeMemory && (forgeMemory.totalTournaments || 0) >= 5 && (() => {
                  // Debug: Log forgeMemory structure
                  //console.log('[Quick Recommendations] forgeMemory:', forgeMemory);
                  //console.log('[Quick Recommendations] totalTournaments:', forgeMemory?.totalTournaments);
                  //console.log('[Quick Recommendations] byCategory:', forgeMemory?.byCategory);
                  
                  // First, check what's in the general category
                  const generalData = forgeMemory?.byCategory?.general;
                  const generalTotalWins = generalData 
                    ? (generalData.gpt?.tournamentWins || 0) + 
                      (generalData.claude?.tournamentWins || 0) + 
                      (generalData.gemini?.tournamentWins || 0)
                    : 0;
                  //console.log(`[Quick Recommendations] General category: totalWins=${generalTotalWins}`, generalData);
                  
                  // Calculate total wins across all categories (excluding general)
                  const totalCategoryWins = Object.entries(getAllCategories())
                    .filter(([key]) => key !== 'general')
                    .reduce((sum, [key]) => {
                      const catData = forgeMemory?.byCategory?.[key];
                      if (!catData) return sum;
                      return sum + (catData.gpt?.tournamentWins || 0) + 
                                 (catData.claude?.tournamentWins || 0) + 
                                 (catData.gemini?.tournamentWins || 0);
                    }, 0);
                  
                  //console.log(`[Quick Recommendations] Total category wins (excluding general): ${totalCategoryWins}`);
                  //console.log(`[Quick Recommendations] Global tournament wins: ${forgeMemory?.totalTournaments || 0}`);
                  
                  // If most tournaments are in general or uncategorized, use global stats
                  const useGlobalStats = totalCategoryWins < (forgeMemory?.totalTournaments || 0) * 0.3;
                  //console.log(`[Quick Recommendations] Use global stats: ${useGlobalStats}`);
                  
                  const recommendations = Object.entries(getAllCategories())
                    .filter(([key]) => {
                      // Include general if it has enough data OR if we're using global stats
                      if (key === 'general') {
                        return useGlobalStats || generalTotalWins >= 3;
                      }
                      return true;
                    })
                    .map(([key, cat]) => {
                      // For global stats, use global data instead of category data
                      const catData = useGlobalStats && key === 'general' 
                        ? forgeMemory?.global 
                        : forgeMemory?.byCategory?.[key];
                        
                      if (!catData) {
                        //console.log(`[Quick Recommendations] No data for category: ${key}`);
                        return null;
                      }
                      
                      const totalWins = (catData.gpt?.tournamentWins || 0) + 
                                       (catData.claude?.tournamentWins || 0) + 
                                       (catData.gemini?.tournamentWins || 0);

                      //console.log(`[Quick Recommendations] Category ${key}: totalWins=${totalWins}`, {
                      //  gpt: catData.gpt?.tournamentWins || 0,
                      //  claude: catData.claude?.tournamentWins || 0,
                      //  gemini: catData.gemini?.tournamentWins || 0
                      //});

                      // Skip if no tournament wins in this category
                      if (totalWins < 3) {
                        //console.log(`[Quick Recommendations] Skipping ${key}: totalWins (${totalWins}) < 3`);
                        return null;
                      }
                      
                      const rec = getRecommendation(catData);
                      //console.log(`[Quick Recommendations] getRecommendation for ${key}:`, rec);
                      
                      // If getRecommendation returns null or has no model, try to determine best model manually
                      if (!rec || !rec.model) {
                        //console.log(`[Quick Recommendations] Manual calculation for ${key}`);
                        // Calculate win rates manually
                        const winRates = {
                          gpt: totalWins > 0 ? Math.round(((catData.gpt?.tournamentWins || 0) / totalWins) * 100) : 0,
                          claude: totalWins > 0 ? Math.round(((catData.claude?.tournamentWins || 0) / totalWins) * 100) : 0,
                          gemini: totalWins > 0 ? Math.round(((catData.gemini?.tournamentWins || 0) / totalWins) * 100) : 0
                        };
                        
                        //console.log(`[Quick Recommendations] Win rates for ${key}:`, winRates);
                        
                        // Find best model
                        let bestModel = 'gpt';
                        let bestRate = winRates.gpt;
                        if (winRates.claude > bestRate) {
                          bestRate = winRates.claude;
                          bestModel = 'claude';
                        }
                        if (winRates.gemini > bestRate) {
                          bestRate = winRates.gemini;
                          bestModel = 'gemini';
                        }
                        
                        //console.log(`[Quick Recommendations] Best model for ${key}: ${bestModel} (${bestRate}%)`);
                        
                        // Only include if we have a clear winner
                        if (bestRate > 0) {
                          const IconComponent = {
                            coding: CodeIcon,
                            creative: PenToolIcon,
                            analysis: SearchIcon,
                            math: CalculatorIcon,
                            business: BriefcaseIcon,
                            general: BarChart3Icon
                          }[key] || LightbulbIcon;
                          
                          // Use "Overall" label for general when using global stats
                          const displayCat = useGlobalStats && key === 'general' 
                            ? { ...cat, name: 'Overall' }
                            : cat;
                          
                          return {
                            key,
                            cat: displayCat,
                            rec: {
                              model: bestModel,
                              winRate: bestRate,
                              confidence: 'low',
                              isDominant: false
                            },
                            IconComponent
                          };
                        }
                        return null;
                      }

                      const IconComponent = {
                        coding: CodeIcon,
                        creative: PenToolIcon,
                        analysis: SearchIcon,
                        math: CalculatorIcon,
                        business: BriefcaseIcon,
                        general: BarChart3Icon
                      }[key] || LightbulbIcon;

                      // Use "Overall" label for general when using global stats
                      const displayCat = useGlobalStats && key === 'general' 
                        ? { ...cat, name: 'Overall' }
                        : cat;

                      //console.log(`[Quick Recommendations] Including ${key} with recommendation:`, rec);
                      return { key, cat: displayCat, rec, IconComponent };
                    })
                    .filter(item => item !== null);
                  
                  //console.log('[Quick Recommendations] Final recommendations array:', recommendations);

                  return (
                    <div className="glass border border-purple-500/30 rounded-xl p-5 bg-purple-500/5">
                      <div className="flex items-center gap-2 mb-4">
                        <ZapIcon className="h-5 w-5 text-purple-400" />
                        <h4 className="font-bold text-white">Quick Recommendations</h4>
                      </div>
                      {recommendations.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                          {recommendations.map(({ key, cat, rec, IconComponent }) => (
                            <div
                              key={key}
                              className="flex items-center gap-3 p-3 rounded-lg bg-slate-800/50 border border-slate-700/30"
                            >
                              <IconComponent className="h-4 w-4 text-slate-400 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div className="text-sm text-white font-medium truncate">{cat.name}</div>
                                <div className="text-xs text-slate-400">
                                  <span className={
                                    rec.model === 'gpt' ? 'text-emerald-400' :
                                    rec.model === 'claude' ? 'text-orange-400' : 'text-blue-400'
                                  }>
                                    {rec.model.toUpperCase()}
                                  </span>
                                  {' wins '}
                                  <span className="text-white">{rec.winRate || 0}%</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-sm text-slate-400 text-center py-4">
                          No recommendations yet. Run at least 3 tournaments in any category to see recommendations.
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Info Box */}
                <div className="glass border border-blue-500/30 rounded-xl p-5 shadow-lg bg-blue-500/5">
                  <div className="flex gap-3">
                    <InfoIcon className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-slate-300 space-y-2">
                      <p><strong className="text-white">How it works:</strong> Forge Memory automatically categorizes your prompts and tracks which AI performs best for different types of tasks.</p>
                      <p><strong className="text-white">Recommendations:</strong> After 3+ tournaments in a category, you'll see data-driven recommendations for which AI to start with.</p>
                      <p><strong className="text-white">Win Rate:</strong> Percentage of tournaments won by each AI in the selected category.</p>
                      <p><strong className="text-white">Note:</strong> Forge Memory is designed exclusively for tournament mode and does not account for Solo Mode interactions.</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Costs Category */}
            {activeCategory === 'costs' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                    <DollarSignIcon className="h-5 w-5 text-amber-400" />
                    Token Usage & Costs
                  </h3>
                  <p className="text-slate-400 text-sm mb-6">
                    Track your AI token consumption and estimated costs across Solo and Tournament modes.
                  </p>
                </div>

                {tokenCostsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-400"></div>
                    <span className="ml-3 text-slate-400">Loading cost data...</span>
                  </div>
                ) : tokenCosts ? (
                  <>
                    {/* Total Cost Summary */}
                    <div className="glass border border-amber-500/30 rounded-xl p-5 bg-amber-500/5">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 rounded-lg bg-amber-500/20">
                          <DollarSignIcon className="h-6 w-6 text-amber-400" />
                        </div>
                        <div>
                          <h4 className="text-lg font-bold text-white">Total Estimated Cost</h4>
                          <p className="text-slate-400 text-sm">All-time usage across all modes</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-amber-400">
                            ${tokenCosts.totals?.costUSD?.toFixed(4) || '0.0000'}
                          </div>
                          <div className="text-xs text-slate-400">USD</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-amber-300">
                            €{tokenCosts.totals?.costEUR?.toFixed(4) || '0.0000'}
                          </div>
                          <div className="text-xs text-slate-400">EUR</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-cyan-400">
                            {((tokenCosts.totals?.totalTokens || 0) / 1000).toFixed(1)}K
                          </div>
                          <div className="text-xs text-slate-400">Total Tokens</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-purple-400">
                            {((tokenCosts.totals?.inputTokens || 0) / 1000).toFixed(1)}K / {((tokenCosts.totals?.outputTokens || 0) / 1000).toFixed(1)}K
                          </div>
                          <div className="text-xs text-slate-400">Input / Output</div>
                        </div>
                      </div>
                    </div>

                    {/* Mode Breakdown */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Solo Mode */}
                      <div className="glass border border-emerald-500/30 rounded-xl p-5">
                        <div className="flex items-center gap-2 mb-4">
                          <UserIcon className="h-5 w-5 text-emerald-400" />
                          <h4 className="font-bold text-emerald-300">Solo Mode</h4>
                        </div>
                        {tokenCosts.modes?.solo ? (
                          <>
                            <div className="mb-4">
                              <div className="flex justify-between text-sm mb-1">
                                <span className="text-slate-400">Total Cost</span>
                                <span className="text-emerald-400 font-bold">${tokenCosts.modes.solo.costUSD?.toFixed(4) || '0.0000'}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-slate-400">Tokens</span>
                                <span className="text-white">{((tokenCosts.modes.solo.totalTokens || 0) / 1000).toFixed(1)}K</span>
                              </div>
                            </div>
                            <div className="space-y-2 text-xs">
                              {tokenCosts.modes.solo.agents && Object.entries(tokenCosts.modes.solo.agents).map(([agent, data]) => (
                                <div key={agent} className="flex justify-between items-center p-2 bg-slate-800/50 rounded">
                                  <span className="text-slate-300 capitalize">{agent}</span>
                                  <span className="text-slate-400">
                                    {(data.inputTokens / 1000).toFixed(1)}K in / {(data.outputTokens / 1000).toFixed(1)}K out
                                  </span>
                                </div>
                              ))}
                            </div>
                          </>
                        ) : (
                          <div className="text-slate-500 text-sm text-center py-4">No solo mode usage yet</div>
                        )}
                      </div>

                      {/* Tournament Mode */}
                      <div className="glass border border-cyan-500/30 rounded-xl p-5">
                        <div className="flex items-center gap-2 mb-4">
                          <TrophyIcon className="h-5 w-5 text-cyan-400" />
                          <h4 className="font-bold text-cyan-300">Tournament Mode</h4>
                        </div>
                        {tokenCosts.modes?.tournament ? (
                          <>
                            <div className="mb-4">
                              <div className="flex justify-between text-sm mb-1">
                                <span className="text-slate-400">Total Cost</span>
                                <span className="text-cyan-400 font-bold">${tokenCosts.modes.tournament.costUSD?.toFixed(4) || '0.0000'}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-slate-400">Tokens</span>
                                <span className="text-white">{((tokenCosts.modes.tournament.totalTokens || 0) / 1000).toFixed(1)}K</span>
                              </div>
                            </div>
                            <div className="space-y-2 text-xs">
                              {tokenCosts.modes.tournament.agents && Object.entries(tokenCosts.modes.tournament.agents).map(([agent, data]) => (
                                <div key={agent} className="flex justify-between items-center p-2 bg-slate-800/50 rounded">
                                  <span className="text-slate-300 capitalize">{agent}</span>
                                  <span className="text-slate-400">
                                    {(data.inputTokens / 1000).toFixed(1)}K in / {(data.outputTokens / 1000).toFixed(1)}K out
                                  </span>
                                </div>
                              ))}
                            </div>
                          </>
                        ) : (
                          <div className="text-slate-500 text-sm text-center py-4">No tournament mode usage yet</div>
                        )}
                      </div>
                    </div>

                    {/* Refresh Button */}
                    <div className="flex justify-center">
                      <button
                        onClick={loadTokenCosts}
                        className="px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm flex items-center gap-2 transition-all"
                      >
                        <RotateCcwIcon className="h-4 w-4" />
                        Refresh Cost Data
                      </button>
                    </div>

                    {/* Info Note */}
                    <div className="glass border border-slate-600/30 rounded-xl p-4 bg-slate-800/30">
                      <div className="flex gap-3">
                        <InfoIcon className="h-5 w-5 text-slate-400 flex-shrink-0" />
                        <div className="text-sm text-slate-400">
                          <p>Costs are estimated based on token usage and current API pricing. Actual costs may vary.</p>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-12">
                    <DollarSignIcon className="h-12 w-12 text-slate-600 mx-auto mb-4" />
                    <p className="text-slate-400">No cost data available yet</p>
                    <p className="text-slate-500 text-sm">Start using the AI to track token consumption</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-slate-800/95 backdrop-blur-sm border-t border-slate-700 p-4 sm:p-6">
          <button
            onClick={onClose}
            className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold py-2.5 sm:py-3 px-4 sm:px-6 rounded-xl hover:from-cyan-600 hover:to-blue-700 transition-all shadow-lg hover:shadow-cyan-500/50 text-sm sm:text-base"
          >
            Save & Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;
